import { getEndpointUrl } from './api-config';

const ACCESS_TOKEN_KEY = 'flux_access_token';
const REFRESH_TOKEN_KEY = 'flux_refresh_token';
const REFRESH_TIMEOUT = 15000;

interface TokenPayload {
  exp?: number;
  sub?: string;
  email?: string;
  [key: string]: unknown;
}

class AuthService {
  private static instance: AuthService;
  private tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;
  private sessionExpiredHandler: (() => void) | null = null;
  private emailNotVerifiedHandler: (() => void) | null = null;

  private constructor() {}

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public setSessionExpiredHandler(handler: (() => void) | null): void {
    this.sessionExpiredHandler = handler;
  }

  public setEmailNotVerifiedHandler(handler: (() => void) | null): void {
    this.emailNotVerifiedHandler = handler;
  }

  public fireEmailNotVerified(): void {
    this.emailNotVerifiedHandler?.();
  }

  public getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  public getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  public setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    this.startRefreshInterval();
  }

  public clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }

  public isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const expiry = this.getTokenExpiry(token);
    if (expiry === null) return true;
    return Date.now() < expiry * 1000;
  }

  private parseToken(token: string): TokenPayload | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(decodeURIComponent(escape(window.atob(base64))));
    } catch {
      return null;
    }
  }

  private getTokenExpiry(token: string): number | null {
    const payload = this.parseToken(token);
    if (!payload || typeof payload.exp !== 'number') return null;
    return payload.exp;
  }

  public startRefreshInterval(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    this.tokenRefreshInterval = setInterval(async () => {
      const token = this.getToken();
      if (!token) {
        this.clearTokens();
        return;
      }

      const expiry = this.getTokenExpiry(token);
      if (expiry === null) return;

      const secondsUntilExpiry = expiry - Math.floor(Date.now() / 1000);
      if (secondsUntilExpiry < 60) {
        await this.refreshAccessToken();
      }
    }, 30000);
  }

  public async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      this.sessionExpiredHandler?.();
      return false;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT);

    try {
      const url = getEndpointUrl('auth', 'refresh');
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        signal: controller.signal,
      });

      if (!response.ok) {
        this.clearTokens();
        this.sessionExpiredHandler?.();
        return false;
      }

      const data = await response.json();
      if (data.accessToken && data.refreshToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        return true;
      }

      this.clearTokens();
      this.sessionExpiredHandler?.();
      return false;
    } catch {
      this.clearTokens();
      this.sessionExpiredHandler?.();
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

export const authService = AuthService.getInstance();
